<template>
  <div class="state-diagram">
    <svg :viewBox="viewBox" class="diagram-svg">
      <defs>
        <!-- Arrow markers -->
        <marker id="arrowhead"
markerWidth="10" markerHeight="7" refX="9"
refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#404060" />
        </marker>
        <marker id="arrowhead-active"
markerWidth="10" markerHeight="7" refX="9"
refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#60a5fa" />
        </marker>

        <!-- Glow filter for active state -->
        <filter id="glow"
x="-50%" y="-50%" width="200%"
height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <!-- Green glow for review_ready -->
        <filter id="glow-green"
x="-50%" y="-50%" width="200%"
height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- Transition arrows -->
      <g class="transitions">
        <path
          v-for="(transition, index) in transitions"
          :key="index"
          :d="transition.path"
          class="transition-path"
          :class="{
            active: isActiveTransition(transition.from, transition.to),
            'reverse-path': transition.isReverse,
          }"
          :marker-end="
            isActiveTransition(transition.from, transition.to) ? 'url(#arrowhead-active)' : 'url(#arrowhead)'
          "
        />
      </g>

      <!-- State nodes -->
      <g class="nodes">
        <g
          v-for="state in states"
          :key="state.id"
          class="state-node"
          :class="{
            active: currentState === state.id,
            'is-review-ready': state.id === 'review_ready',
          }"
          :transform="`translate(${state.x}, ${state.y})`"
        >
          <rect
            :width="state.width"
            :height="nodeHeight"
            :rx="6"
            :ry="6"
            class="node-bg"
            :filter="currentState === state.id ? (state.id === 'review_ready' ? 'url(#glow-green)' : 'url(#glow)') : ''"
          />
          <text :x="state.width / 2" :y="nodeHeight / 2 + 1" class="node-label">
            {{ state.label }}
          </text>
        </g>
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import type { AIAssistantState } from "@/composables/useStateMachine";

const props = defineProps<{
  currentState: AIAssistantState;
  previousState?: AIAssistantState | null;
}>();

// Track previous state for transition highlighting
const lastTransition = ref<{ from: AIAssistantState; to: AIAssistantState } | null>(null);

watch(
  () => props.currentState,
  (newState, oldState) => {
    if (oldState && newState !== oldState) {
      lastTransition.value = { from: oldState as AIAssistantState, to: newState };
      // Clear transition highlight after animation
      setTimeout(() => {
        lastTransition.value = null;
      }, 2000);
    }
  }
);

// SVG dimensions
const svgWidth = 340;
const svgHeight = 420;
const nodeHeight = 36;
const viewBox = `0 0 ${svgWidth} ${svgHeight}`;

// State node positions and sizes
const states = computed(() => [
  { id: "idle" as const, label: "idle", x: 120, y: 15, width: 100 },
  { id: "form_template_selection" as const, label: "form_template_selection", x: 50, y: 75, width: 240 },
  { id: "device_selection" as const, label: "device_selection", x: 80, y: 135, width: 180 },
  { id: "recording" as const, label: "recording", x: 110, y: 195, width: 120 },
  { id: "processing" as const, label: "processing", x: 110, y: 255, width: 120 },
  { id: "error" as const, label: "error", x: 255, y: 255, width: 70 },
  { id: "review_ready" as const, label: "review_ready", x: 95, y: 315, width: 150 },
]);

// Helper to get state node position
const getStatePos = (stateId: AIAssistantState) => {
  const state = states.value.find((s) => s.id === stateId);
  if (!state) return { x: 0, y: 0, width: 100 };
  return state;
};

// Valid transitions from the state machine
const validTransitions: Array<{ from: AIAssistantState; to: AIAssistantState }> = [
  // From idle
  { from: "idle", to: "form_template_selection" },
  { from: "idle", to: "processing" },
  { from: "idle", to: "review_ready" },
  // From form_template_selection
  { from: "form_template_selection", to: "device_selection" },
  { from: "form_template_selection", to: "idle" },
  // From device_selection
  { from: "device_selection", to: "recording" },
  { from: "device_selection", to: "idle" },
  // From recording
  { from: "recording", to: "processing" },
  { from: "recording", to: "idle" },
  // From processing
  { from: "processing", to: "review_ready" },
  { from: "processing", to: "error" },
  // From review_ready
  { from: "review_ready", to: "idle" },
  { from: "review_ready", to: "form_template_selection" },
  // From error
  { from: "error", to: "idle" },
  { from: "error", to: "form_template_selection" },
];

// Generate path for a transition
const generatePath = (from: AIAssistantState, to: AIAssistantState): string => {
  const fromState = getStatePos(from);
  const toState = getStatePos(to);

  // Calculate center points
  const fromCenterX = fromState.x + fromState.width / 2;
  const fromCenterY = fromState.y + nodeHeight / 2;
  const toCenterX = toState.x + toState.width / 2;
  const toCenterY = toState.y + nodeHeight / 2;

  // Determine connection points based on relative positions
  let startX: number, startY: number, endX: number, endY: number;

  // Forward flow (downward)
  if (toState.y > fromState.y) {
    startX = fromCenterX;
    startY = fromState.y + nodeHeight;
    endX = toCenterX;
    endY = toState.y;
  }
  // Backward flow (upward) - return paths
  else if (toState.y < fromState.y) {
    // Route around the left side for return paths
    startX = fromState.x;
    startY = fromCenterY;
    endX = toState.x;
    endY = toCenterY;

    // Create a curved path going left then up
    const offsetX = -30;
    return `M ${startX} ${startY} 
            C ${startX + offsetX} ${startY}, 
              ${endX + offsetX} ${endY}, 
              ${endX} ${endY}`;
  }
  // Same row (horizontal) - for error transition
  else {
    if (toState.x > fromState.x) {
      startX = fromState.x + fromState.width;
      startY = fromCenterY;
      endX = toState.x;
      endY = toCenterY;
    } else {
      startX = fromState.x;
      startY = fromCenterY;
      endX = toState.x + toState.width;
      endY = toCenterY;
    }
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }

  // Default: straight line with slight curve for forward paths
  const midY = (startY + endY) / 2;
  return `M ${startX} ${startY} 
          C ${startX} ${midY}, 
            ${endX} ${midY}, 
            ${endX} ${endY}`;
};

// Check if this is a "return" transition (going backwards/upwards)
const isReverseTransition = (from: AIAssistantState, to: AIAssistantState): boolean => {
  const fromState = getStatePos(from);
  const toState = getStatePos(to);
  return toState.y < fromState.y;
};

// Compute all transition paths
const transitions = computed(() =>
  validTransitions.map((t) => ({
    from: t.from,
    to: t.to,
    path: generatePath(t.from, t.to),
    isReverse: isReverseTransition(t.from, t.to),
  }))
);

// Check if a transition is the currently active one
const isActiveTransition = (from: AIAssistantState, to: AIAssistantState): boolean => {
  if (!lastTransition.value) return false;
  return lastTransition.value.from === from && lastTransition.value.to === to;
};
</script>

<style scoped>
.state-diagram {
  width: 100%;
  height: 100%;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.diagram-svg {
  width: 100%;
  height: 100%;
  max-width: 340px;
}

/* Transition paths */
.transition-path {
  fill: none;
  stroke: #353550;
  stroke-width: 1.5;
  transition: stroke 0.3s ease, stroke-width 0.3s ease;
}

.transition-path.reverse-path {
  stroke: #2a2a40;
  stroke-dasharray: 4 3;
}

.transition-path.active {
  stroke: #60a5fa;
  stroke-width: 2;
  animation: flowAnimation 0.8s ease-out;
}

@keyframes flowAnimation {
  0% {
    stroke-dasharray: 0 150;
  }
  100% {
    stroke-dasharray: 150 0;
  }
}

/* State nodes - default inactive style */
.node-bg {
  fill: rgba(30, 30, 50, 0.9);
  stroke: #404060;
  stroke-width: 1.5;
  transition: all 0.25s ease;
}

/* Active state - blue accent */
.state-node.active .node-bg {
  fill: rgba(40, 45, 70, 0.95);
  stroke: #60a5fa;
  stroke-width: 2;
}

/* Special highlight for review_ready */
.state-node.is-review-ready .node-bg {
  fill: rgba(20, 50, 35, 0.9);
  stroke: #2d5a40;
}

.state-node.is-review-ready.active .node-bg {
  fill: rgba(16, 80, 50, 0.95);
  stroke: #10b981;
  stroke-width: 2;
}

/* Node labels */
.node-label {
  fill: #8090a0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-weight: 500;
  text-anchor: middle;
  dominant-baseline: middle;
  pointer-events: none;
  transition: fill 0.25s ease;
}

.state-node.active .node-label {
  fill: #e0e8f0;
  font-weight: 600;
}

.state-node.is-review-ready .node-label {
  fill: #6b9080;
}

.state-node.is-review-ready.active .node-label {
  fill: #6ee7b7;
  font-weight: 600;
}
</style>
